<script lang="ts" generics="ITEM extends Identified<ID>, ID">
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    import type {Identified} from 'src/typings';
    import {getLocalization} from 'src/core';
    import {states} from 'shared/constants';
    import {pipe} from 'core/utils';
    import {strParams} from 'shared/utils/pipes';
    import type {PageableList, PageableListState} from 'shared/utils/reactive-pageable-list';
    import type {Observable} from 'rxjs';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import KpLoadingInline from 'shared/components/kp-loading/KpLoadingInline.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';

    export let pageableList: PageableList<ITEM, ID, unknown>;
    export let bordered = false;
    export let verticallyDivided = true;
    export let isInPaddedPanel = false;
    export let additionalListItemClasses = '';

    const localize = getLocalization();
    let pageableListState: Observable<PageableListState<ITEM, ID, unknown>>;
    $: pageableListState = pageableList.getState$();

    $: isLoadingFirstPage = $pageableListState.currentState === states.PROCESSING && $pageableListState.nextPage.pageNumber === 1;
    $: isLoadingNextPage = $pageableListState.currentState === states.PROCESSING && $pageableListState.nextPage.pageNumber !== 1;
    $: isEmpty = !$pageableListState.isPartiallyLoaded || $pageableListState.loadedSize === 0;
    $: inNotEmpty = !isEmpty;
    $: loadMore = !$pageableListState.isAllLoaded && !isLoadingFirstPage;
    $: hasError = $pageableListState.currentState === states.ERRORED;
</script>

<div class="kp-paginated-panel-list-container">
    <div class="pagination-content" aria-live="polite">
        {#if isLoadingFirstPage}
            <KpLoadingBlock size="sm"/>
        {/if}

        {#if !isLoadingFirstPage && !hasError && isEmpty}
            <div role="status" class="text-muted text-center">
                {localize(/* @kp-localization commons.ZadnePolozky */ 'commons.ZadnePolozky')}
            </div>
        {/if}

        {#if !isLoadingFirstPage && inNotEmpty}
            <ul class="kp-paginated-panel-list"
                class:bordered-list={bordered}
                class:vertically-divided-list={verticallyDivided}
                class:in-padded-panel-list={isInPaddedPanel}
                class:has-next-page={loadMore}
                class:no-list-items={isEmpty}>

                {#each $pageableListState.loadedItems ?? [] as item}
                    <li class="paginated-list-item {additionalListItemClasses}">
                        <slot {item}/>
                    </li>
                {/each}
            </ul>
        {/if}

        {#if hasError}
            <p role="status" class="text-muted text-center">{$pageableListState.errorMessage}</p>
        {/if}

    </div>

    {#if loadMore}
        <KpButton isDisabled={isLoadingNextPage} on:click={() => pageableList.loadNextPage()}>
            {#if isLoadingNextPage}
                <KpLoadingInline size="xs"/>
            {:else}
                <IconedContent icon="search">
                    {#if !$pageableListState.nextPage.pageSize}
                        {localize(/* @kp-localization vysledky.NacistDalsichX */ 'vysledky.NacistDalsi')}
                    {:else}
                        {pipe(
                            localize(/* @kp-localization vysledky.NacistDalsichX */ 'vysledky.NacistDalsichX'),
                            strParams($pageableListState.nextPage.pageSize.toString())
                        )}
                    {/if}
                </IconedContent>
            {/if}
        </KpButton>
    {/if}
</div>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    .kp-paginated-panel-list-container {
        display: flex;
        flex-direction: column;
        gap: @panel-body-padding;

        .kp-paginated-panel-list {
            &.bordered-list {
                border-radius: @border-radius-base;
                border: 1px solid @panel-default-border;
                overflow: hidden;
            }

            &.vertically-divided-list .paginated-list-item {
                border-top: 1px solid @panel-default-border;

                &:first-child {
                    border-top: none;
                }
            }

            &.in-padded-panel-list {
                margin: calc(-1 * @panel-body-padding);
            }

            &.has-next-page {
                margin-bottom: 0;
                border-bottom: 1px solid @panel-default-border;
            }

            &.no-list-items {
                margin-bottom: @panel-body-padding;
            }

            .paginated-list-item {
                padding: @panel-heading-padding;
            }
        }
    }
</style>